// Vuex module for managing account-scoped LLM provider tokens.
// Each token record shape (as returned by API controller):
// {
//   id: Number,
//   provider_name: String,
//   active: Boolean,
//   has_token: Boolean,        // indicates if encrypted token is stored
//   created_at: String (ISO),
//   updated_at: String (ISO)
// }
//
// Notes:
// - The API never returns the decrypted access token.
// - To set or rotate a token you must pass { access_token: 'plain-token' } in create/update.
// - provider_name must be one of the server supported providers.
// - active can be toggled independently.
//
// Exposed getters:
//   getTokens                -> all tokens
//   getUIFlags               -> ui flags
//   getActiveTokens          -> only active tokens
//   getTokenById(id)         -> token by id
//   getTokenByProvider(name) -> first token matching provider_name (lowercased)
//   hasProvider(name)        -> boolean
//
// Actions:
//   get()                      -> load all tokens
//   create(payload)            -> create new token (payload: { provider_name, access_token?, active? })
//   update({ id, ...attrs })   -> update token (supports access_token rotation)
//   rotate({ id, access_token })
//   activate(id)
//   deactivate(id)
//   delete(id)
//
// UI Flags:
//   isFetching, isCreating, isUpdating, isDeleting
//
// This module follows the same mutation helper pattern used by existing modules (labels, etc).

import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import LlmProviderTokensAPI from '../../api/llmProviderTokens';

const types = {
  SET_UI_FLAG: 'SET_LLM_PROVIDER_TOKEN_UI_FLAG',
  SET_TOKENS: 'SET_LLM_PROVIDER_TOKENS',
  ADD_TOKEN: 'ADD_LLM_PROVIDER_TOKEN',
  EDIT_TOKEN: 'EDIT_LLM_PROVIDER_TOKEN',
  DELETE_TOKEN: 'DELETE_LLM_PROVIDER_TOKEN',
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getTokens(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getActiveTokens(_state) {
    return _state.records.filter(r => r.active);
  },
  getTokenById: _state => id => {
    return _state.records.find(r => r.id === Number(id));
  },
  getTokenByProvider: _state => provider => {
    if (!provider) return undefined;
    const p = provider.toString().toLowerCase();
    return _state.records.find(r => r.provider_name === p);
  },
  hasProvider: (_state, g) => provider => {
    return !!g.getTokenByProvider(provider);
  },
};

export const actions = {
  async get({ commit }) {
    commit(types.SET_UI_FLAG, { isFetching: true });
    try {
      const response = await LlmProviderTokensAPI.get();
      const tokens = (response.data.payload || []).sort((a, b) =>
        a.provider_name.localeCompare(b.provider_name)
      );
      commit(types.SET_TOKENS, tokens);
    } catch {
      // ignore – consumer can inspect uiFlags
    } finally {
      commit(types.SET_UI_FLAG, { isFetching: false });
    }
  },

  async create({ commit }, payload) {
    commit(types.SET_UI_FLAG, { isCreating: true });
    try {
      const response = await LlmProviderTokensAPI.create(payload);
      commit(types.ADD_TOKEN, response.data);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Error creating LLM provider token';
      throw new Error(message);
    } finally {
      commit(types.SET_UI_FLAG, { isCreating: false });
    }
  },

  async update({ commit }, { id, ...attrs }) {
    commit(types.SET_UI_FLAG, { isUpdating: true });
    try {
      const response = await LlmProviderTokensAPI.update(id, attrs);
      commit(types.EDIT_TOKEN, response.data);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Error updating LLM provider token';
      throw new Error(message);
    } finally {
      commit(types.SET_UI_FLAG, { isUpdating: false });
    }
  },

  async rotate({ dispatch }, { id, access_token }) {
    // Thin wrapper around update to emphasize token rotation intent.
    if (!access_token) {
      throw new Error('access_token is required to rotate a token');
    }
    return dispatch('update', { id, access_token });
  },

  async activate({ dispatch }, id) {
    return dispatch('update', { id, active: true });
  },

  async deactivate({ dispatch }, id) {
    return dispatch('update', { id, active: false });
  },

  async delete({ commit }, id) {
    commit(types.SET_UI_FLAG, { isDeleting: true });
    try {
      await LlmProviderTokensAPI.delete(id);
      commit(types.DELETE_TOKEN, id);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Error deleting LLM provider token';
      throw new Error(message);
    } finally {
      commit(types.SET_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_TOKENS]: MutationHelpers.set,
  [types.ADD_TOKEN]: MutationHelpers.create,
  [types.EDIT_TOKEN]: MutationHelpers.update,
  [types.DELETE_TOKEN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
